import React, { useState, useCallback } from 'react';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import { store } from 'redux/store';
import { clearFilters } from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import { modelPerformanceApi } from 'services/api/Meteorology/Fields/ModelPerformance';
import useMapData from 'hooks/Meteorology/Fields/ModelPerformance/useMapData';
import useCanQuery from 'hooks/Meteorology/Fields/ModelPerformance/useCanQuery';
import usePeriodType from 'hooks/Meteorology/Fields/ModelPerformance/usePeriodType';
import useAnalysisType from 'hooks/Meteorology/Fields/ModelPerformance/useAnalysisType';
import useVariable from 'hooks/Meteorology/Fields/ModelPerformance/useVariable';
import useOptions from 'hooks/Meteorology/Fields/ModelPerformance/useOptions';
import RoundsSection from './RoundsSection';
import FreeRoundsSection from './FreeRoundsSection';
import AnalysisPeriodSection from './AnalysisPeriodSection';

import './index.scss';

type SheetSection = 'period' | 'analysisType' | 'variable' | 'rounds' | 'analysisPeriod' | '';

export default function ControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const [openSection, setOpenSection] = useState<SheetSection>('');
  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const { periodType, setPeriodType } = usePeriodType();
  const { analysisType, setAnalysisType } = useAnalysisType();
  const { variable, setVariable } = useVariable();
  const { analysisTypes, variables } = useOptions();

  const { triggerMapDataRequest, isLoading } = useMapData();
  const { canQuery } = useCanQuery();

  const isFieldDisabled = !periodType;

  const periodTypeLabel = periodType === 'preset' ? 'Pré-definido' : undefined;
  const periodTypeLabelFree = periodType === 'free' ? 'Livre' : undefined;
  const periodValueText = periodTypeLabel ?? periodTypeLabelFree;

  const analysisTypeLabel = analysisTypes.find((t) => t.id === analysisType)?.label;
  const variableLabel = variables.find((v) => v.id === variable)?.label;

  const isQueryButtonDisabled = !canQuery || isLoading;

  const handleClearFilters = useCallback(() => {
    store.dispatch(clearFilters());
    store.dispatch(modelPerformanceApi.util.resetApiState());
    setOpenSection('');
  }, []);

  const handleQuery = useCallback(() => {
    triggerMapDataRequest();
    dismiss();
  }, [triggerMapDataRequest, dismiss]);

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="mp-mobile-control-panel-sheet"
    >
      <div className="mp-mobile-sheet-body">
        <Section
          label="Período"
          valueText={periodValueText}
          open={openSection === 'period'}
          onToggle={toggleSection('period')}
        >
          <OptionRow
            label="Pré-definido"
            selected={periodType === 'preset'}
            onSelect={() => setPeriodType('preset')}
          />
          <OptionRow
            label="Livre"
            selected={periodType === 'free'}
            onSelect={() => setPeriodType('free')}
          />
        </Section>

        <Section
          label="Tipo de análise"
          valueText={analysisTypeLabel}
          open={openSection === 'analysisType'}
          onToggle={toggleSection('analysisType')}
        >
          {isFieldDisabled && (
            <p className="mp-mobile-sheet-hint">Selecione o período primeiro</p>
          )}
          {!isFieldDisabled && analysisTypes.map((type) => (
            <OptionRow
              key={type.id}
              label={type.label}
              selected={type.id === analysisType}
              onSelect={() => setAnalysisType(type.id)}
            />
          ))}
        </Section>

        <Section
          label="Variável"
          valueText={variableLabel}
          open={openSection === 'variable'}
          onToggle={toggleSection('variable')}
        >
          {isFieldDisabled && (
            <p className="mp-mobile-sheet-hint">Selecione o período primeiro</p>
          )}
          {!isFieldDisabled && variables.map((v) => (
            <OptionRow
              key={v.id}
              label={v.label}
              selected={v.id === variable}
              onSelect={() => setVariable(v.id)}
            />
          ))}
        </Section>

        {periodType === 'preset' && (
          <RoundsSection open={openSection === 'rounds'} onToggle={toggleSection('rounds')} />
        )}

        {periodType === 'free' && (
          <>
            <FreeRoundsSection
              open={openSection === 'rounds'}
              onToggle={toggleSection('rounds')}
            />
            <AnalysisPeriodSection
              open={openSection === 'analysisPeriod'}
              onToggle={toggleSection('analysisPeriod')}
            />
          </>
        )}
      </div>

      <div className="mp-mobile-sheet-footer">
        <SheetActionButton variant="secondary" onClick={handleClearFilters}>
          Limpar filtros
        </SheetActionButton>
        <SheetActionButton
          variant="confirm"
          disabled={isQueryButtonDisabled}
          loading={isLoading}
          onClick={handleQuery}
        >
          Realizar consulta
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
